
import { useMemo, useEffect } from "react";
import moment from "moment";
import { DeviceType } from "../../../../../Enums/DeviceType";
import EndpointStatusChart from "../../../../SiteExplorer/Pages/Endpoints/EndpointStatusChart";
import { enMessages } from "../../../../../messages/en-US";
import { useLocalization } from '@progress/kendo-react-intl';
import useChartStore from "../../../../../Zustand/chartStore";
import NoDataPage from "../../../../SiteExplorer/Pages/NoDataPage";
import DiagnosticsTable from "../DiagnosticsTable";


interface Props {
    deviceData: any;
    eventFilter: any;
    setEventFilter: any;
    deviceSerialNumber: string;
    deviceId: string;
    deviceType: string;
    chartParentHeight: number;
    isDiagnostics: boolean;
    deviceEventData: any;
    deviceNotifications?: any;
    deviceFeatures: any;
    /** Timeline: compact chart without side legend (e.g. dashboard tile embedded). */
    isSimpleChart?: boolean;
    /** Timeline: show legend column layout (fullscreen). Default true for Health dashboard. */
    isLegendColumn?: boolean;
    /** Timeline: State events to render as a series; only meaningful when isStateSupported is true. */
    stateEvents?: any[];
    /** Timeline: show State series when device supports grease pump connectivity. */
    isStateSupported?: boolean;
}

export default function EndpointHealthDetails(props: Props) {

  const localizationService = useLocalization();
  const timelineIsSimple = props.isSimpleChart === true;
  const timelineLegendColumn = props.isLegendColumn !== false;

  // The chart snapshots its data on mount, so it must remount when the date range changes
  const rangeKey = `${props.eventFilter?.startDate?.getTime?.() ?? ''}:${props.eventFilter?.endDate?.getTime?.() ?? ''}`;

  const { viewType, setViewType } = useChartStore((state) => ({ 
    viewType: state.viewType, 
    setViewType: state.setViewType 
  }));

  // Set viewType to 'line' when component mounts
  useEffect(() => {
    setViewType('line');
  }, [setViewType]);


const statusData = {
   dateFilter: props.eventFilter,
   timestamps: props.deviceNotifications?.timestamps,
   alerts: {
        timestamps: props.deviceNotifications?.timestamps,
        yValues: new Array(props.deviceNotifications?.timestamps?.length).fill(1),
        metadata: props.deviceNotifications?.metadata
    },
    statusData: {
        id: "",
        timestamps: [],
        yValues: [],
        metadata:[
            {
                color: "#FFFFFF",
                reasonCode: 0
            },
            {
                color: "#00A2E8",
                reasonCode: 0
            }
        ]
    }
}

  // Create timeline data from endpoint events
  const endpointTimelineData = useMemo(() => {
    const seriesData: any[] = [];

    // Add Flow Events only if flow is supported
    if (props.deviceFeatures?.isFlowSupported) {
      seriesData.push({
        id: "FlowEventsId",
        name: localizationService.toLanguageString('custom.flowEvents', enMessages.custom.flowEvents),
        color: "rgba(50, 53, 67,1)",
        timestamps: props.deviceEventData.deviceFlowEvents?.map((event: any) => Math.floor(moment(event.timestamp).valueOf() / 1000)) || [],
        yAxisValueArray: new Array(props.deviceEventData.deviceFlowEvents?.length || 0).fill(props.deviceFeatures?.isTemperatureSupported && props.deviceFeatures?.isPressureSupported ? 6 : !props.deviceFeatures?.isTemperatureSupported && props.deviceFeatures?.isPressureSupported ? 5 : props.deviceFeatures?.isTemperatureSupported && !props.deviceFeatures?.isPressureSupported ? 5 : 4),
        tooltips: [{
          name: localizationService.toLanguageString('custom.flowCc', enMessages.custom.flowCc),
          data: props.deviceEventData.deviceFlowEvents?.map((event: any) => event.volumeDispensed) || [],
          color: "rgba(50, 53, 67,1)"
        },
        {
          name: localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion),
          data: props.deviceEventData.deviceFlowEvents?.map((event: any) => event.firmwareVersion) || [],
          color: "rgba(50, 53, 67,1)"
        }],
      });
    }

    // Add Pressure Events only if pressure is supported
    if (props.deviceFeatures?.isPressureSupported) {
      seriesData.push({
        id: "PressureEventsId",
        name: localizationService.toLanguageString('custom.pressureEvents', enMessages.custom.pressureEvents),
        color: "#45B7D1",
        timestamps: props.deviceEventData.devicePressureEvents?.map((event: any) => Math.floor(moment(event.timestamp).valueOf() / 1000)) || [],
        yAxisValueArray: new Array(props.deviceEventData.devicePressureEvents?.length || 0).fill(props.deviceFeatures?.isTemperatureSupported ? 5 : 4),
        tooltips: [{
          name: localizationService.toLanguageString('custom.pressureMean', enMessages.custom.pressureMean),
          data: props.deviceEventData.devicePressureEvents?.map((event: any) => event.pressureMean) || [],
          color: "#45B7D1"
        },
        {
          name: localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion),
          data: props.deviceEventData.devicePressureEvents?.map((event: any) => event.firmwareVersion) || [],
          color: "#45B7D1"
        }],
      });
    }

    // Add Diagnostic Events (always included as it's a basic feature)
    seriesData.push({
      id: "DiagnosticEventsId",
      name: localizationService.toLanguageString('custom.diagnosticEvents', enMessages.custom.diagnosticEvents),
      color: "#A349A4",
      timestamps: props.deviceEventData.deviceDiagnosticEvents?.map((event: any) => Math.floor(moment(event.timestamp).valueOf() / 1000)) || [],
      yAxisValueArray: new Array(props.deviceEventData.deviceDiagnosticEvents?.length || 0).fill(props.deviceFeatures?.isTemperatureSupported ? 4 : 3),
      tooltips: [
        {
          name: localizationService.toLanguageString('custom.temperatureInC', enMessages.custom.temperatureInC),
          data: props.deviceEventData.deviceDiagnosticEvents?.map((event: any) => event.temperature) || [],
          color: "#A349A4"
        },
        {
          name: localizationService.toLanguageString('custom.voltage', enMessages.custom.voltage),
          data: props.deviceEventData.deviceDiagnosticEvents?.map((event: any) => event.voltage / 1000) || [],
          color: "#A349A4"
        },
        {
          name: localizationService.toLanguageString('custom.battery', enMessages.custom.battery),
          data: props.deviceEventData.deviceDiagnosticEvents?.map((event: any) => event.coulombCount) || [],
          color: "#A349A4"
        },
        {
          name: localizationService.toLanguageString('custom.slot', enMessages.custom.slot),
          data: props.deviceEventData.deviceDiagnosticEvents?.map((event: any) => event.slot) || [],
          color: "#A349A4"
        },
        {
          name: localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion),
          data: props.deviceEventData.deviceDiagnosticEvents?.map((event: any) => event.firmwareVersion) || [],
          color: "#A349A4"
        }
      ],
    });

    // Add Heartbeat Events (always included as it's a basic feature)
    const heartbeatTooltips = [
      {
        name: localizationService.toLanguageString('custom.rssiDbm', enMessages.custom.rssiDbm),
        data: props.deviceEventData.deviceHeartbeatEvents?.map((event: any) => event.rssi) || [],
        color: "#FF7F27"
      },
      {
        name: localizationService.toLanguageString('custom.endpointRssi', enMessages.custom.endpointRssi),
        data: props.deviceEventData.deviceHeartbeatEvents?.map((event: any) => event.endpointRssi) || [],
        color: "#FF7F27"
      },
      {
        name: localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion),
        data: props.deviceEventData.deviceHeartbeatEvents?.map((event: any) => event.firmwareVersion) || [],
        color: "#FF7F27"
      }
    ];

    // Only add cumulative tooltip if device type is not bulkflow or hme
    if (props.deviceData.deviceType !== DeviceType.EndpointBF && props.deviceData.deviceType !== DeviceType.EndpointMP2) {
      heartbeatTooltips.unshift({
        name: localizationService.toLanguageString('custom.cumulative', enMessages.custom.cumulative),
        data: props.deviceEventData.deviceHeartbeatEvents?.map((event: any) => event.cumulative) || [],
        color: "#FF7F27"
      });
    }

    seriesData.push({
      id: "HeartbeatEventsId",
      name: localizationService.toLanguageString('custom.heartbeatEvents', enMessages.custom.heartbeatEvents),
      color: "#FF7F27",
      timestamps: props.deviceEventData.deviceHeartbeatEvents?.map((event: any) => Math.floor(moment(event.timestamp).valueOf() / 1000)) || [],
      yAxisValueArray: new Array(props.deviceEventData.deviceHeartbeatEvents?.length || 0).fill(props.deviceFeatures?.isTemperatureSupported ? 3 : 2),
      tooltips: heartbeatTooltips,
    });

    // Add Temperature Events only if temperature is supported
    if (props.deviceFeatures?.isTemperatureSupported) {
      seriesData.push({
        id: "TemperatureEventsId",
        name: localizationService.toLanguageString('custom.temperatureEvents', enMessages.custom.temperatureEvents),
        color: "#E74C3C",
        timestamps: props.deviceEventData.deviceTemperatureEvents?.map((event: any) => Math.floor(moment(event.timestamp).valueOf() / 1000)) || [],
        yAxisValueArray: new Array(props.deviceEventData.deviceTemperatureEvents?.length || 0).fill(2),
        tooltips: [{
          name: localizationService.toLanguageString('custom.temperatureInC', enMessages.custom.temperatureInC),
          data: props.deviceEventData.deviceTemperatureEvents?.map((event: any) => event.temperature) || [],
          color: "#E74C3C"
        },
        {
          name: localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion),
          data: props.deviceEventData.deviceTemperatureEvents?.map((event: any) => event.firmwareVersion) || [],
          color: "#E74C3C"
        }],
      });
    }

    // Add Button Events (always included as it's a basic feature)
    seriesData.push({
      id: "ButtonEventsId",
      name: localizationService.toLanguageString('custom.buttonEvents', enMessages.custom.buttonEvents),
      color: "#D4A017",
      timestamps: props.deviceEventData.deviceButtonEvents?.map((event: any) => Math.floor(moment(event.timestamp).valueOf() / 1000)) || [],
      yAxisValueArray: new Array(props.deviceEventData.deviceButtonEvents?.length || 0).fill(1),
      tooltips: [{
        name: localizationService.toLanguageString('custom.pressedTime', enMessages.custom.pressedTime),
        data: props.deviceEventData.deviceButtonEvents?.map((event: any) => event.pressedTime) || [],
        color: "#D4A017"
      },
      {
        name: localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion),
        data: props.deviceEventData.deviceButtonEvents?.map((event: any) => event.firmwareVersion) || [],
        color: "#D4A017"
      }],
    });

    // Add State Events when device supports grease pump connectivity (sits above other rows)
    if (props.isStateSupported) {
      const stateColor = "#1E8449";
      const stateY = seriesData.reduce(
        (max, s) => Math.max(max, s.yAxisValueArray?.[0] ?? 0),
        0
      ) + 1;
      seriesData.push({
        id: "StateEventsId",
        name: localizationService.toLanguageString('custom.greasePumpEvents', enMessages.custom.greasePumpEvents),
        color: stateColor,
        timestamps: props.stateEvents?.map((event: any) => Math.floor(moment(event.timestamp).valueOf() / 1000)) || [],
        yAxisValueArray: new Array(props.stateEvents?.length || 0).fill(stateY),
        tooltips: [
          {
            name: localizationService.toLanguageString('custom.state', enMessages.custom.state),
            data: props.stateEvents?.map((event: any) => event.value === 1 ? 'On' : 'Off') || [],
            color: stateColor,
          },
          {
            name: localizationService.toLanguageString('custom.gateway', enMessages.custom.gateway),
            data: props.stateEvents?.map((event: any) => event.serialNumber) || [],
            color: stateColor,
          },
          {
            name: localizationService.toLanguageString('custom.channel', enMessages.custom.channel),
            data: props.stateEvents?.map((event: any) => event.channel) || [],
            color: stateColor,
          },
          {
            name: localizationService.toLanguageString('custom.firmwareVersion', enMessages.custom.firmwareVersion),
            data: props.stateEvents?.map((event: any) => event.firmwareVersion) || [],
            color: stateColor,
          },
        ],
      });
    }

    return {
      seriesData,
      dateFilter: props.eventFilter,
      aggregator: 'none',
      strokeThickness: 1
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.deviceEventData,
    props.eventFilter,
    props.deviceFeatures,
    props.deviceData,
    props.stateEvents,
    props.isStateSupported,
  ]);

  const endpointDiagnosticsData = useMemo(() => {
    return {
        timestampsDiagnostics: props.deviceEventData.deviceDiagnosticEvents?.map(event => Math.floor(moment(event.timestamp).valueOf() / 1000)) || [],
        temperatureData: props.deviceEventData.deviceDiagnosticEvents?.map((event: any) => event.temperature) || [],
        voltageData: props.deviceEventData.deviceDiagnosticEvents?.map((event: any) => event.voltage / 1000) || [],
        batteryData: props.deviceEventData.deviceDiagnosticEvents?.map((event: any) => event.coulombCount ?? NaN) || [],
        batterySlots: props.deviceEventData.deviceDiagnosticEvents?.map((event: any) => event.slot ?? null) || [],
        timestampsMetrics: props.deviceEventData.deviceHeartbeatEvents?.map(event => Math.floor(moment(event.timestamp).valueOf() / 1000)) || [],
        rssiData: props.deviceEventData.deviceHeartbeatEvents?.map((event: any) => event.rssi) || [],
        endpointRssiData: props.deviceEventData.deviceHeartbeatEvents?.map((event: any) => event.endpointRssi) || [],
        cumulativeData: (props.deviceData.deviceType !== DeviceType.EndpointBF && props.deviceData.deviceType !== DeviceType.EndpointMP2) 
          ? props.deviceEventData.deviceHeartbeatEvents?.map((event: any) => event.cumulative) || []
          : undefined,
        dateFilter: props.eventFilter,
        timestamps: props.deviceEventData.deviceFlowEvents?.map(event => Math.floor(moment(event.timestamp).valueOf() / 1000)) || [],
        volumeDispensedData: props.deviceEventData.deviceFlowEvents?.map((event: any) => event.volumeDispensed) || [],
        strokeThickness: 1,
        aggregator: 'none',
        pressureEvents: props.deviceFeatures?.isPressureSupported ? {
            endpointName: props.deviceEventData.devicePressureEvents?.[0]?.serialNumber || '',
            pressureDataMax: props.deviceEventData.devicePressureEvents?.map((event: any) => event.pressureMax) || [],
            pressureDataMean: props.deviceEventData.devicePressureEvents?.map((event: any) => event.pressureMean) || [],
            pressureDataMin: props.deviceEventData.devicePressureEvents?.map((event: any) => event.pressureMin) || [],
            timestamps: props.deviceEventData.devicePressureEvents?.map(event => Math.floor(moment(event.timestamp).valueOf() / 1000)) || [],
        } : undefined,
        temperatureEvents: props.deviceFeatures?.isTemperatureSupported ? {
            timestamps: props.deviceEventData.deviceTemperatureEvents?.map(event => Math.floor(moment(event.timestamp).valueOf() / 1000)) || [],
            temperatures: props.deviceEventData.deviceTemperatureEvents?.map((event: any) => event.temperature) || [],
        } : undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.deviceEventData, props.eventFilter, props.deviceData]);

  // Create combined table data structure
  const combinedTableData = useMemo(() => {
    const allEvents: any[] = [];

    // Add diagnostic events
    if (props.deviceEventData.deviceDiagnosticEvents) {
      props.deviceEventData.deviceDiagnosticEvents.forEach((event: any) => {
        allEvents.push({
          timestamp: event.timestamp,
          synced: event.synced || event.timestamp,
          temperature: event.temperature,
          diagnosticTemperature: event.temperature,
          voltage: event.voltage / 1000,
          eventType: 'Diagnostic',
          rssi: null,
          endpointRssi: null,
          cumulative: null,
          volumeDispensed: null,
          pressureMax: null,
          pressureMean: null,
          pressureMin: null,
          button: null,
          coulombCount: event.coulombCount,
          slot: event.slot
        });
      });
    }

    // Add heartbeat events
    if (props.deviceEventData.deviceHeartbeatEvents) {
      props.deviceEventData.deviceHeartbeatEvents.forEach((event: any) => {
        allEvents.push({
          timestamp: event.timestamp,
          synced: event.synced || event.timestamp,
          temperature: null,
          diagnosticTemperature: null,
          voltage: null,
          eventType: 'Heartbeat',
          rssi: event.rssi,
          endpointRssi: event.endpointRssi,
          cumulative: (props.deviceData.deviceType !== DeviceType.EndpointBF && props.deviceData.deviceType !== DeviceType.EndpointMP2) ? event.cumulative : null,
          volumeDispensed: null,
          pressureMax: null,
          pressureMean: null,
          pressureMin: null,
          button: null
        });
      });
    }

    // Add flow events
    if (props.deviceEventData.deviceFlowEvents) {
      props.deviceEventData.deviceFlowEvents.forEach((event: any) => {
        allEvents.push({
          timestamp: event.timestamp,
          synced: event.synced || event.timestamp,
          temperature: null,
          diagnosticTemperature: null,
          voltage: null,
          eventType: 'Flow',
          rssi: null,
          endpointRssi: null,
          cumulative: null,
          volumeDispensed: event.volumeDispensed,
          pressureMax: null,
          pressureMean: null,
          pressureMin: null,
          button: null
        });
      });
    }

    // Add pressure events
    if (props.deviceEventData.devicePressureEvents) {
      props.deviceEventData.devicePressureEvents.forEach((event: any) => {
        allEvents.push({
          timestamp: event.timestamp,
          synced: event.synced || event.timestamp,
          temperature: null,
          diagnosticTemperature: null,
          voltage: null,
          eventType: 'Pressure',
          rssi: null,
          endpointRssi: null,
          cumulative: null,
          volumeDispensed: null,
          pressureMax: event.pressureMax,
          pressureMean: event.pressureMean,
          pressureMin: event.pressureMin,
          button: null
        });
      });
    }

    // Add temperature events
    if (props.deviceEventData.deviceTemperatureEvents) {
      props.deviceEventData.deviceTemperatureEvents.forEach((event: any) => {
        allEvents.push({
          timestamp: event.timestamp,
          synced: event.synced || event.timestamp,
          temperature: event.temperature,
          diagnosticTemperature: null,
          voltage: null,
          eventType: 'Temperature',
          rssi: null,
          endpointRssi: null,
          cumulative: null,
          volumeDispensed: null,
          pressureMax: null,
          pressureMean: null,
          pressureMin: null,
          button: null
        });
      });
    }

    // Add button events
    if (props.deviceEventData.deviceButtonEvents) {
      props.deviceEventData.deviceButtonEvents.forEach((event: any) => {
        allEvents.push({
          timestamp: event.timestamp,
          synced: event.synced || event.timestamp,
          temperature: null,
          diagnosticTemperature: null,
          voltage: null,
          eventType: 'Button',
          rssi: null,
          endpointRssi: null,
          cumulative: null,
          volumeDispensed: null,
          pressureMax: null,
          pressureMean: null,
          pressureMin: null,
          button: event.pressedTime
        });
      });
    }

    return allEvents;
  }, [props.deviceEventData, props.deviceData]);

  return (
    <div className={
      props.isDiagnostics || timelineLegendColumn ? 'legend-column' : 'flex h-full min-h-0 flex-col'
    }>
      {endpointTimelineData.seriesData.some(series => series.timestamps.length > 0) ? (
        props.isDiagnostics === false ? (
              <EndpointStatusChart
                chartType="timeline"
                key={`${props.deviceId}-timeline-${rangeKey}`}
                statusChartData={statusData}
                timelineData={endpointTimelineData}
                isSimpleChart={timelineIsSimple}
                chartParentHeight={props.chartParentHeight}
                isLegendColumn={timelineLegendColumn}
              />
            ) : (
              <>
              {viewType !== 'table' ? (  
              <EndpointStatusChart
                chartType="diagnostics"
                key={`${props.deviceId}-diagnostics-${rangeKey}`}
                statusChartData={statusData} 
                diagnosticsDataChart={endpointDiagnosticsData} 
                isSimpleChart={false} 
                chartParentHeight={props.chartParentHeight}
                addHitTestRadius={true}
                isLegendColumn={true}
              />
              ) : (
                <DiagnosticsTable
                  key={`${props.deviceId}-diagnostics-table`}
                  events={combinedTableData}
                  aggregator="none"
                  endpointSerialNo={props.deviceSerialNumber}
                  chartParentHeight={props.chartParentHeight}
                  deviceFeatures={props.deviceFeatures}
                  deviceType={props.deviceData.deviceType}
                />
              )}
              </>
            )
          ) : (
            <div className="flex justify-center items-center h-full">
              <NoDataPage />
            </div>
          )}
    </div>
  );
}